import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Security,
  Warning,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  DirectionsCar,
  Person,
  Refresh,
  ExpandMore,
  Speed,
  LocalGasStation,
  Schedule
} from '@mui/icons-material';
import apiClient from '../../api';

const FleetSafetyDashboard = () => {
  const [safetyScore, setSafetyScore] = useState(null);
  const [harshEvents, setHarshEvents] = useState([]);
  const [collisionEvents, setCollisionEvents] = useState([]);
  const [safetyEvents, setSafetyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days

  useEffect(() => {
    loadSafetyData();
  }, [dateRange]);

  const loadSafetyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const startTime = startDate.toISOString();
      const endTime = endDate.toISOString();

      // Load all safety data in parallel
      const [scoreResponse, harshResponse, collisionResponse, eventsResponse] = await Promise.allSettled([
        apiClient.get(`/samsara/safety/score?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/safety/harsh-events?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/safety/collisions?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/safety/events?startTime=${startTime}&endTime=${endTime}`)
      ]);

      if (scoreResponse.status === 'fulfilled') {
        setSafetyScore(scoreResponse.value.data);
      } else {
        console.warn('Failed to load safety score:', scoreResponse.reason);
      }

      if (harshResponse.status === 'fulfilled') {
        setHarshEvents(harshResponse.value.data.data || []);
      } else {
        console.warn('Failed to load harsh events:', harshResponse.reason);
      }

      if (collisionResponse.status === 'fulfilled') {
        setCollisionEvents(collisionResponse.value.data.data || []);
      } else {
        console.warn('Failed to load collision events:', collisionResponse.reason);
      }

      if (eventsResponse.status === 'fulfilled') {
        setSafetyEvents(eventsResponse.value.data.data || []);
      } else {
        console.warn('Failed to load safety events:', eventsResponse.reason);
      }
    } catch (err) {
      console.error('Error loading safety data:', err);
      setError('Failed to load safety data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSafetyData();
    setRefreshing(false);
  };

  const getSafetyScoreColor = (score) => {
    if (score === null || score === undefined) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'error';
      case 'medium':
      case 'moderate':
        return 'warning';
      case 'low':
      case 'minor':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toLocaleString();
  };

  const getTopVehiclesByEvents = () => {
    const vehicleEvents = {};
    [...harshEvents, ...collisionEvents, ...safetyEvents].forEach(event => {
      const vehicleId = event.vehicleId || event.vehicle?.id;
      if (vehicleId) {
        if (!vehicleEvents[vehicleId]) {
          vehicleEvents[vehicleId] = {
            vehicleId,
            vehicleName: event.vehicle?.name || `Vehicle ${vehicleId}`,
            count: 0,
            events: []
          };
        }
        vehicleEvents[vehicleId].count++;
        vehicleEvents[vehicleId].events.push(event);
      }
    });

    return Object.values(vehicleEvents)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const getRecentEvents = () => {
    return [...harshEvents, ...collisionEvents, ...safetyEvents]
      .sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
      .slice(0, 10);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
              Fleet Safety Dashboard
            </Typography>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Safety Score Overview */}
      {safetyScore && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fleet Safety Score
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box textAlign="center">
                  <Typography variant="h2" color="primary">
                    {safetyScore.score || safetyScore.overallScore || 'N/A'}
                  </Typography>
                  <Typography variant="h6" color="textSecondary">
                    Overall Safety Score
                  </Typography>
                  <Box mt={2}>
                    <LinearProgress
                      variant="determinate"
                      value={safetyScore.score || safetyScore.overallScore || 0}
                      color={getSafetyScoreColor(safetyScore.score || safetyScore.overallScore)}
                      sx={{ height: 12, borderRadius: 6 }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Safety Metrics
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Harsh Events"
                        secondary={formatNumber(safetyScore.harshEvents || harshEvents.length)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Collision Events"
                        secondary={formatNumber(safetyScore.collisionEvents || collisionEvents.length)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Safety Events"
                        secondary={formatNumber(safetyScore.totalEvents || safetyEvents.length)}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Safety Statistics */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color="warning" />
                <Typography variant="h6">
                  {harshEvents.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Harsh Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color="error" />
                <Typography variant="h6">
                  {collisionEvents.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Collision Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Security color="primary" />
                <Typography variant="h6">
                  {safetyEvents.length}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Total Safety Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <DirectionsCar color="info" />
                <Typography variant="h6">
                  {getTopVehiclesByEvents().length}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Vehicles with Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* High-Risk Vehicles */}
      {getTopVehiclesByEvents().length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="error">
              <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
              High-Risk Vehicles
            </Typography>
            <List>
              {getTopVehiclesByEvents().map((vehicle, index) => (
                <ListItem key={vehicle.vehicleId} divider={index < getTopVehiclesByEvents().length - 1}>
                  <ListItemIcon>
                    <DirectionsCar color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={vehicle.vehicleName}
                    secondary={`${vehicle.count} safety events`}
                  />
                  <Chip
                    label={`${vehicle.count} events`}
                    color="error"
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Recent Safety Events */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Safety Events
          </Typography>
          <List>
            {getRecentEvents().map((event, index) => (
              <ListItem key={index} divider={index < getRecentEvents().length - 1}>
                <ListItemIcon>
                  {event.type === 'harsh' ? (
                    <Speed color="warning" />
                  ) : event.type === 'collision' ? (
                    <Warning color="error" />
                  ) : (
                    <Security color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">
                        {event.type || 'Safety Event'}
                      </Typography>
                      <Chip
                        label={event.severity || 'Unknown'}
                        color={getSeverityColor(event.severity)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Vehicle: {event.vehicle?.name || event.vehicleId || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Time: {formatDate(event.timestamp || event.createdAt)}
                      </Typography>
                      {event.location && (
                        <Typography variant="body2" color="textSecondary">
                          Location: {event.location.latitude?.toFixed(4)}, {event.location.longitude?.toFixed(4)}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          {getRecentEvents().length === 0 && (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="textSecondary">
                No recent safety events
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Safety Trends */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Safety Trends
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Harsh Events by Type</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {harshEvents.reduce((acc, event) => {
                      const type = event.eventType || event.type || 'Unknown';
                      acc[type] = (acc[type] || 0) + 1;
                      return acc;
                    }, {})}
                    {Object.entries(harshEvents.reduce((acc, event) => {
                      const type = event.eventType || event.type || 'Unknown';
                      acc[type] = (acc[type] || 0) + 1;
                      return acc;
                    }, {})).map(([type, count]) => (
                      <ListItem key={type}>
                        <ListItemText
                          primary={type}
                          secondary={`${count} events`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Event Severity Distribution</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {['high', 'medium', 'low'].map(severity => {
                      const count = [...harshEvents, ...collisionEvents, ...safetyEvents]
                        .filter(event => (event.severity || '').toLowerCase() === severity).length;
                      return (
                        <ListItem key={severity}>
                          <ListItemText
                            primary={severity.charAt(0).toUpperCase() + severity.slice(1)}
                            secondary={`${count} events`}
                          />
                          <Chip
                            label={count}
                            color={getSeverityColor(severity)}
                            size="small"
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* No Data Message */}
      {!safetyScore && harshEvents.length === 0 && collisionEvents.length === 0 && safetyEvents.length === 0 && (
        <Card>
          <CardContent>
            <Alert severity="info">
              No safety data available. Configure Samsara integration to view fleet safety dashboard.
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FleetSafetyDashboard;
